/**
 * Assignment Modal (moved + adapted for DC-1648).
 *
 * Modal dialog for assigning employees to FAIR persona governance roles.
 * Consumes the canonical `Persona` shape; role dot color via scoped CSS
 * class (persona-topology.css tokens).
 */

import React, { useState, useEffect } from 'react';
import type { Persona } from '../../../types/triangulation';
import type { TopologyEmployee } from './types';
import { roleClassOf } from './types';

interface AssignmentModalProps {
  isOpen: boolean;
  persona: Persona | null;
  onClose: () => void;
  onAssign: (personaId: string, employeeId: string, notes: string) => Promise<void>;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

export const AssignmentModal: React.FC<AssignmentModalProps> = ({
  isOpen,
  persona,
  onClose,
  onAssign,
  authFetch,
}) => {
  const [employees, setEmployees] = useState<TopologyEmployee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // DC-189: Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Fetch employees when modal opens
  useEffect(() => {
    if (isOpen) {
      setFetchingEmployees(true);
      authFetch('/api/employees')
        .then((res) => res.json())
        .then((data) => {
          const activeEmployees = (data.employees || []).filter((e: TopologyEmployee) => e.is_active);
          setEmployees(activeEmployees);
          setFetchingEmployees(false);
        })
        .catch((err) => {
          console.error('[AssignmentModal] Failed to fetch employees:', err);
          setFetchingEmployees(false);
        });

      // Reset form
      setSelectedEmployee('');
      setNotes('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!persona || !selectedEmployee) {
      setError('Please select a team member');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onAssign(persona.id, selectedEmployee, notes);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create assignment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !persona) return null;

  const description = persona.description ?? persona.full_description ?? '';

  return (
    <div
      className="pt-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="assignment-modal-title"
    >
      <div className="pt-modal-content assignment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pt-modal-header">
          <h2 id="assignment-modal-title">Assign Team Member</h2>
          <button className="pt-modal-close" onClick={onClose} aria-label="Close modal">&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="pt-modal-body">
            {/* Persona Info */}
            <div className="assignment-persona-info">
              <div className="persona-preview">
                <span className={`persona-dot ${roleClassOf(persona.primary_role)}`} />
                <div>
                  <strong>{persona.name}</strong>
                  <span className="persona-role-label">{persona.primary_role ?? 'Unassigned role'}</span>
                </div>
              </div>
              {description && (
                <p className="persona-definition-preview">
                  {description.slice(0, 150)}
                  {description.length > 150 ? '…' : ''}
                </p>
              )}
            </div>

            {error && <div className="form-error">{error}</div>}

            {/* Employee Selection */}
            <div className="pt-form-group">
              <label htmlFor="employee-select">
                Select Team Member <span className="required">*</span>
              </label>
              {fetchingEmployees ? (
                <div className="loading-inline">Loading team members...</div>
              ) : employees.length === 0 ? (
                <div className="empty-employees">
                  <p>No team members available.</p>
                  <a href="/settings/team" className="link-to-team">
                    Add team members first
                  </a>
                </div>
              ) : (
                <select
                  id="employee-select"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  required
                >
                  <option value="">-- Select a team member --</option>
                  {employees.map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {emp.display_name} ({emp.email})
                      {emp.job_title ? ` - ${emp.job_title}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Notes */}
            <div className="pt-form-group">
              <label htmlFor="assignment-notes">Notes (Optional)</label>
              <textarea
                id="assignment-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this assignment..."
                rows={3}
              />
            </div>
          </div>

          <div className="pt-modal-footer">
            <button
              type="button"
              className="pt-btn secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="pt-btn primary"
              disabled={loading || !selectedEmployee || employees.length === 0}
            >
              {loading ? 'Assigning...' : 'Assign Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
